import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ButtonContained from '../../StyledComponents/ButtonContained';
import ButtonOutlined from '../../StyledComponents/ButtonOutlined';
import { enqueueSnackbar } from 'notistack';
import httpRequest from '../../../httpRequest';

const AttachmentTable = ({ invoiceId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newDocument, setNewDocument] = useState({ name: '', description: '', file: null, filename: '', category: null });
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await httpRequest.get(
        `${process.env.REACT_APP_BACKEND_URL}/documents?invoice_id=${invoiceId}`
      );
      setDocuments(response.data);
    } catch (error) {
      setError('Failed to fetch documents from DMS.');
      enqueueSnackbar('Document retrieval failed', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/document-categories`);
      setCategories(response.data);
    } catch (error) {
      setError('Failed to fetch categories');
      enqueueSnackbar('Failed to fetch categories', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
  }, [invoiceId]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.replace('data:', '').replace(/^.+,/, '');
      setNewDocument((prev) => ({ ...prev, file: base64String, filename: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    setFormSubmitted(true);

    if (!newDocument.file || !newDocument.name || !newDocument.category) {
      enqueueSnackbar('Please fill all required fields', { variant: 'warning' });
      return;
    }

    const payload = {
      name: newDocument.name,
      description: newDocument.description,
      filename: newDocument.filename,
      file: newDocument.file,
      document_category_id: newDocument.category.id,
      invoice_id: invoiceId,
    };

    try {
      const response = await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/documents`, payload);
      enqueueSnackbar('Document uploaded successfully', { variant: 'success' });
      fetchDocuments();
      setFormSubmitted(false);
      setUploadDialogOpen(false);
      setNewDocument({ name: '', description: '', file: null, filename: '', category: null });
    } catch (error) {
      enqueueSnackbar('Failed to upload document', { variant: 'error' });
    }
  };

  const handleViewDetails = (document) => {
    setSelectedDocument(document);
    setDetailsDialogOpen(true);
  };

  const handleEdit = (document) => {
    const category = categories.find((c) => c.id === document.category.id);
    setSelectedDocument({ ...document, category });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedDocument.name || !selectedDocument.category) {
      enqueueSnackbar('Please fill all required fields', { variant: 'warning' });
      return;
    }

    const { id, name, description } = selectedDocument;
    try {
      await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/documents/${id}`, {
        name,
        description,
        document_category_id: selectedDocument.category.id,
      });
      enqueueSnackbar('Document updated successfully', { variant: 'success' });
      fetchDocuments();
      setEditDialogOpen(false);
    } catch (error) {
      enqueueSnackbar('Failed to update document', { variant: 'error' });
    }
  };

  const handleDelete = (document) => {
    setSelectedDocument(document);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/documents/${selectedDocument.id}`);
      enqueueSnackbar('Document deleted successfully', { variant: 'success' });
      fetchDocuments();
      setDeleteDialogOpen(false);
    } catch (error) {
      enqueueSnackbar('Failed to delete document', { variant: 'error' });
    }
  };

  const handleViewAttachment = async (doc) => {
    try {
      const response = await httpRequest.get(
        `${process.env.REACT_APP_BACKEND_URL}/documents/${doc.id}/file`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);

      // Determine the file extension from the filename
      const fileExtension = doc.filename.split('.').pop().toLowerCase();
      const viewableExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif'];

      if (viewableExtensions.includes(fileExtension)) {
        // Open PDFs and images in a popup window (800x600)
        const popup = window.open(
          url,
          '_blank',
          'width=600,height=800,scrollbars=yes,resizable=yes'
        );
        if (!popup) {
          enqueueSnackbar('Popup blocked. Please allow popups for this site.', { variant: 'warning' });
        }
      } else {
        // Download other file types
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch attachment', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', marginTop: "20px" }}>
        <CircularProgress />
      </div>
    );
  }
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <>
      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle sx={{ fontFamily: 'SapFiori72, sans-serif' }}>Upload Attachment</DialogTitle>
        <DialogContent sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
          <TextField
            fullWidth
            label="Document Name"
            value={newDocument.name}
            onChange={(e) => setNewDocument({ ...newDocument, name: e.target.value })}
            sx={{ mb: 2, fontFamily: 'SapFiori72, sans-serif' }}
            error={formSubmitted && !newDocument.name}
            helperText={formSubmitted && !newDocument.name ? 'Document Name is required' : ''}
          />
          <TextField
            fullWidth
            label="Description"
            value={newDocument.description}
            onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
            sx={{ mb: 2, fontFamily: 'SapFiori72, sans-serif' }}
          />
          <FormControl fullWidth sx={{ mb: 2 }} error={formSubmitted && !newDocument.category}>
            <InputLabel sx={{ fontFamily: 'SapFiori72, sans-serif' }}>Category</InputLabel>
            <Select
              value={newDocument.category || ''}
              onChange={(e) => setNewDocument({ ...newDocument, category: e.target.value })}
              sx={{ fontFamily: 'SapFiori72, sans-serif' }}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category} sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            {formSubmitted && !newDocument.category && (
              <FormHelperText sx={{ fontFamily: 'SapFiori72, sans-serif' }}>Category is required</FormHelperText>
            )}
          </FormControl>
          <Button
            variant="contained"
            component="label"
            sx={{ mb: 2, fontFamily: 'SapFiori72, sans-serif' }}
          >
            Upload File
            <input type="file" hidden onChange={handleFileUpload} />
          </Button>
          {newDocument.filename && (
            <Typography sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
              Uploaded File: {newDocument.filename}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
          <ButtonOutlined style={{ marginRight: 8 }} onClick={() => setUploadDialogOpen(false)} color="primary">
            Cancel
          </ButtonOutlined>
          <ButtonContained onClick={handleUpload} color="primary">
            Upload
          </ButtonContained>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle sx={{ fontFamily: 'SapFiori72, sans-serif' }}>Edit Attachment</DialogTitle>
        <DialogContent sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
          <TextField
            fullWidth
            label="Document Name"
            value={selectedDocument?.name || ''}
            onChange={(e) => setSelectedDocument({ ...selectedDocument, name: e.target.value })}
            sx={{ mb: 2, fontFamily: 'SapFiori72, sans-serif' }}
            error={!selectedDocument?.name}
            helperText={!selectedDocument?.name ? 'Document Name is required' : ''}
          />
          <TextField
            fullWidth
            label="Description"
            value={selectedDocument?.description || ''}
            onChange={(e) => setSelectedDocument({ ...selectedDocument, description: e.target.value })}
            sx={{ mb: 2, fontFamily: 'SapFiori72, sans-serif' }}
          />
          <FormControl fullWidth sx={{ mb: 2 }} error={!selectedDocument?.category}>
            <InputLabel sx={{ fontFamily: 'SapFiori72, sans-serif' }}>Category</InputLabel>
            <Select
              value={selectedDocument?.category?.id || ''}
              onChange={(e) => {
                const selectedCategory = categories.find((c) => c.id === e.target.value);
                setSelectedDocument({ ...selectedDocument, category: selectedCategory });
              }}
              sx={{ fontFamily: 'SapFiori72, sans-serif' }}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            {!selectedDocument?.category && (
              <FormHelperText sx={{ fontFamily: 'SapFiori72, sans-serif' }}>Category is required</FormHelperText>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
          <ButtonOutlined style={{ marginRight: 8 }} onClick={() => setEditDialogOpen(false)} color="primary">
            Cancel
          </ButtonOutlined>
          <ButtonContained onClick={handleSave} color="primary">
            Save
          </ButtonContained>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ fontFamily: 'SapFiori72, sans-serif' }}>Delete Document</DialogTitle>
        <DialogContent sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
          Are you sure you want to delete this document?
        </DialogContent>
        <DialogActions sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
          <ButtonOutlined style={{ marginRight: 8 }} onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </ButtonOutlined>
          <ButtonContained onClick={handleDeleteConfirm} color="primary">
            Delete
          </ButtonContained>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)}>
        <DialogTitle sx={{ fontFamily: 'SapFiori72, sans-serif' }}>Document Details</DialogTitle>
        <DialogContent sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
          <Typography sx={{ mb: 1, fontFamily: 'SapFiori72, sans-serif' }}>
            <strong>Name:</strong> {selectedDocument?.name || '-'}
          </Typography>
          <Typography sx={{ mb: 1, fontFamily: 'SapFiori72, sans-serif' }}>
            <strong>Description:</strong> {selectedDocument?.description || '-'}
          </Typography>
          <Typography sx={{ mb: 1, fontFamily: 'SapFiori72, sans-serif' }}>
            <strong>Category:</strong> {selectedDocument?.category?.name || '-'}
          </Typography>
          <Typography sx={{ mb: 1, fontFamily: 'SapFiori72, sans-serif' }}>
            <strong>Uploaded By:</strong> {selectedDocument?.uploadedBy || '-'}
          </Typography>
          <Typography sx={{ mb: 1, fontFamily: 'SapFiori72, sans-serif' }}>
            <strong>Uploaded At:</strong> {selectedDocument?.uploadedAt
              ? new Date(selectedDocument.uploadedAt).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '-'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AttachFileIcon />}
            onClick={() => handleViewAttachment(selectedDocument)}
            sx={{ mt: 2, fontFamily: 'SapFiori72, sans-serif' }}
          >
            View Attachment
          </Button>
        </DialogContent>
        <DialogActions sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
          <ButtonOutlined onClick={() => setDetailsDialogOpen(false)} color="primary">
            Close
          </ButtonOutlined>
        </DialogActions>
      </Dialog>

      {/* Document Table */}
      <TableContainer
        component={Paper}
        sx={{
          p: 2,
          borderRadius: 1,
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          fontFamily: 'SapFiori72, sans-serif',
          mt: 4,
          mb: 4,
        }}
      >
        <Table>
          <TableHead sx={{ backgroundColor: '#354a5f' }}>
            <TableRow>
              <TableCell align="center" sx={{ color: 'white', fontFamily: 'SapFiori72, sans-serif' }}>
                Document Name
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontFamily: 'SapFiori72, sans-serif' }}>
                Description
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontFamily: 'SapFiori72, sans-serif' }}>
                Category
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontFamily: 'SapFiori72, sans-serif' }}>
                Uploaded By
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontFamily: 'SapFiori72, sans-serif' }}>
                Uploaded At
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontFamily: 'SapFiori72, sans-serif' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
                    No documents found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell align="center" sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
                    {doc.name}
                  </TableCell>
                  <TableCell align="center" sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
                    {doc.description || '-'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
                    {doc.category.name}
                  </TableCell>
                  <TableCell align="center" sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
                    {doc.uploadedBy || '-'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontFamily: 'SapFiori72, sans-serif' }}>
                    {doc.uploadedAt
                      ? new Date(doc.uploadedAt).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Button onClick={() => handleViewDetails(doc)}>
                      <VisibilityIcon />
                    </Button>
                    <Button onClick={() => handleEdit(doc)}>
                      <EditIcon />
                    </Button>
                    <Button onClick={() => handleDelete(doc)}>
                      <DeleteIcon />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Button
                  variant="contained"
                  onClick={() => setUploadDialogOpen(true)}
                  sx={{
                    gap: 1,
                    backgroundColor: '#354a5f',
                    '&:hover': {
                      backgroundColor: '#2c3e50',
                    },
                  }}
                >
                  <AddIcon />
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default AttachmentTable;